import classNames from "classnames";
import { ReactNode, useState } from "react";
import { useParams } from "react-router";
import { Constants } from "../../../../../../common/constants";
import {
	ModifyEventRSVPersRequest,
	ModifyRSVPersForEventResponse,
} from "../../../../../../common/sesh_types";
import { RouterInferenceHelpers } from "../../../../../../src/api/trpc/rootRouter";
import { DashboardUtils } from "../../../dashboard_utils";
import { trpc } from "../../../utils/trpc";
import { RemovingThrobber } from "../../removing_throbber";
import { useIsAdmin } from "../hooks/use_is_admin";
import { useRSVPedUsers } from "../hooks/use_rsvped_users";
import { useUserContext } from "../hooks/use_user_context";
import { InlineEmoji } from "../inline_emoji";
import MemberDisplayNameWithTooltip from "../member_display_name/member_display_name_with_tooltip";
import { WithTooltip } from "../tooltips/with_tooltip";
import { RSVPModal } from "./add_rsvp_modal";

export function getOptionStringified(
	option: WebRSVPOption,
	rsvp_count?: number
) {
	const emoji_element = <InlineEmoji option={option} />;
	const option_name = option.name;
	const count =
		rsvp_count != null
			? `(${rsvp_count}${option.limit ? `/${option.limit}` : ""})`
			: null;

	return (
		<span>
			{emoji_element} {option_name}{" "}
			{option.limit != null && count != null && count}
		</span>
	);
}

async function removeRSVPersFromEvent(request_body: ModifyEventRSVPersRequest) {
	return fetch(`/api/remove_rsvpers_from_event`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
		},
		body: JSON.stringify(request_body),
	}).then((response) =>
		response.json().then((value: ModifyRSVPersForEventResponse) => {
			if ("err" in value) {
				DashboardUtils.ShowDefaultErrorAlert(value.err);
				return;
			}

			return value;
		})
	);
}

export type WebRSVPOption =
	RouterInferenceHelpers["event"]["getListItem"]["output"]["rsvp_options"][number];

type RSVPRemoveState = "Unselected" | "Selected" | "Removing";

export function RSVPList(props: {
	option: WebRSVPOption;
	has_event_ended: boolean;
}) {
	const user_context = useUserContext();
	const context = trpc.useContext();

	const [is_modal_active, setModalActive] = useState(false);
	const [selected_ids_to_remove, setSelectedIdsToRemove] = useState<string[]>(
		[]
	);
	const [is_loading_remove, setIsLoadingRemove] = useState(false);

	const is_admin_query = useIsAdmin();

	let { server_id, message_id } = useParams<{
		server_id: string;
		message_id: string;
	}>();

	const rsvpers_query = useRSVPedUsers(
		server_id,
		message_id,
		props.option.id
	);

	const is_max_num_of_rsvpers_selected =
		selected_ids_to_remove.length >= Constants.MODIFY_RSVPERS_LIMIT;

	const is_admin = Boolean(is_admin_query.data);

	const rsvp_rows: JSX.Element[] = [];
	if (rsvpers_query.isSuccess) {
		let index = 0;

		if (rsvpers_query.data.length > 0) {
			for (let i = 0; i < rsvpers_query.data.length; i++) {
				const rsvper = rsvpers_query.data[i];

				let state: RSVPRemoveState = undefined;
				if (selected_ids_to_remove.includes(rsvper.id)) {
					if (is_loading_remove) {
						state = "Removing";
					} else {
						state = "Selected";
					}
				} else {
					state = "Unselected";
				}

				rsvp_rows.push(
					<RSVPRow
						key={index}
						user_id={rsvper.id}
						index={index}
						is_last={i === rsvpers_query.data.length - 1}
						is_disabled={is_loading_remove}
						can_remove={is_admin && !props.has_event_ended}
						remove_state={state}
						onClick={(user_id) => {
							setSelectedIdsToRemove((val) => {
								const existing = val.indexOf(user_id);

								const copy = [...val];
								if (existing > -1) {
									copy.splice(existing, 1);
									return copy;
								}
								if (!is_max_num_of_rsvpers_selected) {
									copy.push(user_id);
								}
								return copy;
							});
						}}
					/>
				);

				if (props.option.limit && i === props.option.limit - 1) {
					index++;
					rsvp_rows.push(
						<RawRSVPRow
							className="px-4 py-3"
							key={index}
							index={index}
							is_last={i === rsvpers_query.data.length - 1}
						>
							<div className="flex w-full flex-col font-bold">
								<span className="mx-auto">Waitlist</span>
							</div>
						</RawRSVPRow>
					);
				}

				index++;
			}
		} else {
			rsvp_rows.push(
				<RawRSVPRow key="None" className="px-4 py-3" index={0} is_last>
					<div className="flex items-center gap-2">None</div>
				</RawRSVPRow>
			);
		}
	} else {
		rsvp_rows.push(
			<RawRSVPRow key="Loading" index={0} is_last>
				<div className="flex items-center gap-2 px-4 py-3">
					<RemovingThrobber />
					<span>Loading...</span>
				</div>
			</RawRSVPRow>
		);
	}

	return (
		<>
			<div className="flex flex-col items-center">
				<div className="flex w-11/12 flex-col sm:w-3/4">
					<div className="bg-base-dark px-4 py-3 font-bold">
						<div className="flex items-center">
							{getOptionStringified(
								props.option,
								rsvpers_query.data?.length
							)}

							<div className="ml-auto flex flex-shrink-0 gap-4 text-lg">
								<WithTooltip
									tooltip={`Add users${
										is_admin
											? ""
											: " (requires Manage Sesh permission)"
									}`}
									style="Info"
								>
									<div
										className={classNames({
											"text-link hover:cursor-pointer hover:text-link-hover":
												is_admin,
											"cursor-not-allowed text-gray-400":
												!is_admin,
											hidden: props.has_event_ended,
										})}
										onClick={() => {
											if (!is_admin_query.data) {
												return;
											}

											setModalActive(true);
										}}
									>
										<i className="fas fa-user-plus"></i>{" "}
										<span className="text-base">Add</span>
									</div>
								</WithTooltip>
							</div>
						</div>
					</div>
					<div className="flex max-h-[50vh] w-full flex-col self-start overflow-y-auto">
						{rsvp_rows}
					</div>
					<div
						className={classNames(
							"ml-auto font-bold text-warning hover:cursor-pointer hover:text-warning-hover",
							{
								invisible:
									selected_ids_to_remove.length === 0 ||
									is_loading_remove ||
									!is_admin ||
									props.has_event_ended,
							}
						)}
						onClick={() => {
							setIsLoadingRemove(true);
							removeRSVPersFromEvent({
								access_token: user_context.access_token,
								token_type: user_context.token_type,
								event_id: message_id,
								guild_id: server_id,
								option_emoji: props.option.emoji,
								rsvpers_to_modify: selected_ids_to_remove,
							}).then((val) => {
								setIsLoadingRemove(false);
								if ("err" in val) {
									return;
								}

								context.event.rsvpedUsers.setData(
									(data) =>
										data.filter(
											(val) =>
												!selected_ids_to_remove.includes(
													val.id
												)
										),
									{
										guild_id: server_id,
										message_id: message_id,
										rsvp_option_id: props.option.id,
									}
								);
								setSelectedIdsToRemove([]);
							});
						}}
						title="Remove users"
					>
						<span className="text-base">
							Remove Selected Users (
							{`${selected_ids_to_remove.length ?? 0}${
								is_max_num_of_rsvpers_selected ? ", MAX" : ""
							}`}
							)
						</span>
					</div>
				</div>
			</div>
			{is_modal_active && (
				<RSVPModal
					closeModal={() => setModalActive(false)}
					current_rsvp_ids={rsvpers_query.data.map((val) => val.id)}
					rsvp_option={props.option}
				/>
			)}
		</>
	);
}

function RSVPRow(props: {
	user_id: string;
	index: number;
	is_last?: boolean;
	remove_state: RSVPRemoveState;
	is_disabled?: boolean;
	can_remove?: boolean;
	onClick: (user_id: string) => void;
}) {
	let remove_element: JSX.Element;

	switch (props.remove_state) {
		case "Unselected":
			remove_element = (
				<i className="fas fa-times opacity-20 group-hover:opacity-100"></i>
			);
			break;
		case "Selected":
			remove_element = (
				<i className="fas fa-times-circle text-warning"></i>
			);
			break;
		case "Removing":
			remove_element = <RemovingThrobber />;
			break;
	}

	return (
		<RawRSVPRow
			className={classNames("group", {
				"cursor-pointer": props.can_remove,
			})}
			index={props.index}
			is_last={props.is_last}
		>
			<div
				className="relative w-full"
				onClick={() => {
					props.is_disabled
						? undefined
						: props.onClick(props.user_id);
				}}
			>
				<div className="flex w-full px-4 py-3">
					<MemberDisplayNameWithTooltip
						user_id={props.user_id}
						include_discriminator
						include_avatar
					/>
					{props.can_remove &&
						(!props.is_disabled ||
							props.remove_state !== "Unselected") && (
							<div className="ml-auto flex w-8 flex-shrink-0 justify-center">
								<span key={props.remove_state}>
									{remove_element}
								</span>
							</div>
						)}
				</div>
				{props.can_remove && props.remove_state !== "Unselected" && (
					<div className="pointer-events-none absolute left-0 top-0 flex h-full w-full bg-warning px-4 py-3 opacity-20"></div>
				)}
			</div>
		</RawRSVPRow>
	);
}

function RawRSVPRow(props: {
	className?: string;
	index: number;
	is_last?: boolean;
	children: ReactNode;
}) {
	return (
		<div
			className={classNames(
				props.className,
				"group w-full border border-base-dark",
				{
					"bg-base-primary": props.index % 2 !== 0,
					"bg-base-light": props.index % 2 === 0,
					"border-b": props.is_last,
					"border-b-0": !props.is_last,
				}
			)}
		>
			{props.children}
		</div>
	);
}
